import {
  DEFAULT_ASSISTANT_LLM_TYPE,
  FALLBACK_ASSISTANT_LLM_TYPE,
  LLMType,
} from "models/enums/LLMType";
import { normalizeModelKey } from "utils/generalUtils";

export const STORAGE_KEY_USER_LLM_SETTINGS = "userLLMSettings";
export const STORAGE_KEY_SONNET_55_MIGRATED = "userLLMSettingsSonnet55Migrated";
export const STORAGE_KEY_OPUS_48_MIGRATED = "userLLMSettingsOpus48Migrated";
export const STORAGE_KEY_HAIKU_55_MIGRATED = "userLLMSettingsHaiku55Migrated";
export const MODEL_MIGRATED_FLAG = "true";

interface IModelNameHolder {
  name?: string;
  displayName?: string;
}

export interface ISavedLlmSettingsMigrationResult {
  savedRaw: string | null;
  hasRemapped: boolean;
  previousLlmType?: string;
  migratedLlmType?: string;
}

type ReplacementMap = ReadonlyMap<string, LLMType>;

interface IModelMigration {
  markerKey: string;
  replacements: ReplacementMap;
}

// Large context has no 5.5 variant, so it maps to the plain 5.5 model.
const SONNET_55_REPLACEMENTS: ReplacementMap = new Map([
  [normalizeModelKey(LLMType.CLAUDE_SONNET_4_5), LLMType.CLAUDE_SONNET_5_5],
  [
    normalizeModelKey(LLMType.CLAUDE_SONNET_4_5_LARGE_CONTEXT),
    LLMType.CLAUDE_SONNET_5_5,
  ],
  [
    normalizeModelKey(LLMType.CLAUDE_SONNET_4_5_WEB_SEARCH),
    LLMType.CLAUDE_SONNET_5_5_WEB_SEARCH,
  ],
]);

// Every older Opus 4.x goes straight to 4.8, never through the versions between.
const OPUS_48_REPLACEMENTS: ReplacementMap = new Map([
  [normalizeModelKey(LLMType.CLAUDE_OPUS_4_5), LLMType.CLAUDE_OPUS_4_8],
  [normalizeModelKey(LLMType.CLAUDE_OPUS_4_6), LLMType.CLAUDE_OPUS_4_8],
  [normalizeModelKey(LLMType.CLAUDE_OPUS_4_7), LLMType.CLAUDE_OPUS_4_8],
]);

const HAIKU_55_REPLACEMENTS: ReplacementMap = new Map([
  [normalizeModelKey(LLMType.CLAUDE_HAIKU_4_5), LLMType.CLAUDE_HAIKU_5_5],
]);

// Each migration has its own marker, so a user who already went through the
// Sonnet one still gets the Opus one once Opus 4.8 becomes visible.
const MODEL_MIGRATIONS: readonly IModelMigration[] = [
  {
    markerKey: STORAGE_KEY_SONNET_55_MIGRATED,
    replacements: SONNET_55_REPLACEMENTS,
  },
  {
    markerKey: STORAGE_KEY_OPUS_48_MIGRATED,
    replacements: OPUS_48_REPLACEMENTS,
  },
  {
    markerKey: STORAGE_KEY_HAIKU_55_MIGRATED,
    replacements: HAIKU_55_REPLACEMENTS,
  },
];

const mergeReplacements = (
  migrations: readonly IModelMigration[],
): ReplacementMap =>
  new Map(migrations.flatMap((migration) => [...migration.replacements]));

const LEGACY_TO_REPLACEMENT_MODEL = mergeReplacements(MODEL_MIGRATIONS);

const hasModelNamed = (
  availableModels: IModelNameHolder[],
  modelName: string,
): boolean =>
  availableModels.some(
    (model) => normalizeModelKey(model.name) === normalizeModelKey(modelName),
  );

const hasAnyReplacementModel = (
  migration: IModelMigration,
  availableModels: IModelNameHolder[],
): boolean => {
  const replacementKeys = new Set(
    Array.from(migration.replacements.values(), normalizeModelKey),
  );
  return availableModels.some((model) =>
    replacementKeys.has(normalizeModelKey(model.name)),
  );
};

const readStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

// The browser can be shared, so the marker is kept per user when known.
const getMigrationMarkerKey = (markerKey: string, userId?: string): string =>
  userId ? `${markerKey}:${userId}` : markerKey;

const markMigrationComplete = (
  migration: IModelMigration,
  userId?: string,
): void => {
  writeStorageItem(
    getMigrationMarkerKey(migration.markerKey, userId),
    MODEL_MIGRATED_FLAG,
  );
};

const isMigrationPending = (
  migration: IModelMigration,
  availableModels: IModelNameHolder[],
  userId?: string,
): boolean =>
  readStorageItem(getMigrationMarkerKey(migration.markerKey, userId)) !==
    MODEL_MIGRATED_FLAG && hasAnyReplacementModel(migration, availableModels);

/**
 * Returns the replacement for a legacy model name (Sonnet 4.5 -> Sonnet 5.5,
 * Opus 4.5 / 4.6 / 4.7 -> Opus 4.8, Haiku 4.5 -> Haiku 5.5), or undefined when the name is not a
 * legacy model. Matching ignores case, spaces and hyphens. Does not check
 * availability.
 */
export const getReplacementModelName = (
  modelName?: string,
  replacements: ReplacementMap = LEGACY_TO_REPLACEMENT_MODEL,
): LLMType | undefined => replacements.get(normalizeModelKey(modelName));

/**
 * Remaps a legacy model name to its replacement, but only when the
 * replacement is in the user's available models. Otherwise the original value
 * is returned untouched (the replacement record may still be a limited canary).
 */
export const remapLegacyModelName = (
  modelName: string,
  availableModels: IModelNameHolder[],
  replacements: ReplacementMap = LEGACY_TO_REPLACEMENT_MODEL,
): string => {
  const replacement = getReplacementModelName(modelName, replacements);
  if (!replacement || !hasModelNamed(availableModels, replacement)) {
    return modelName;
  }
  return replacement;
};

/**
 * Finds the model a template recommends. Templates usually store the model
 * display name (for example "Claude Sonnet 4.5 - Problem Solver"), so the
 * model is resolved first and its name is then remapped to its replacement
 * when the user can see it. Falls back to the originally recommended model
 * otherwise.
 */
export const resolveRecommendedModel = <T extends IModelNameHolder>(
  availableModels: T[],
  recommendedModel?: string,
): T | undefined => {
  const recommendedKey = normalizeModelKey(recommendedModel);
  if (!recommendedKey) {
    return undefined;
  }

  const recommended =
    availableModels.find(
      (model) =>
        normalizeModelKey(model.displayName || model.name) === recommendedKey,
    ) ??
    availableModels.find(
      (model) => normalizeModelKey(model.name) === recommendedKey,
    );

  const remappedName = remapLegacyModelName(
    recommended?.name ?? recommendedModel ?? "",
    availableModels,
  );
  const remapped = availableModels.find(
    (model) =>
      normalizeModelKey(model.name) === normalizeModelKey(remappedName),
  );
  return remapped ?? recommended;
};

/**
 * Picks the model used by chats that send a fixed default instead of the
 * user's selection. Prefers Sonnet 5.5 only when the user can see it; while
 * the models are not loaded yet (or 5.5 is not visible) it uses Sonnet 4.5.
 * Callers must re-evaluate this when the available models change.
 */
export const resolveAssistantLlmType = (
  availableModels: IModelNameHolder[],
): LLMType =>
  hasModelNamed(availableModels, DEFAULT_ASSISTANT_LLM_TYPE)
    ? DEFAULT_ASSISTANT_LLM_TYPE
    : FALLBACK_ASSISTANT_LLM_TYPE;

/**
 * Remaps the llmType inside a saved "userLLMSettings" JSON string. Returns
 * the updated JSON string, or undefined when nothing changed (including
 * unparseable input), so callers only write back when needed.
 */
export const migrateSavedLlmSettings = (
  savedRaw: string | null,
  availableModels: IModelNameHolder[],
  replacements: ReplacementMap = LEGACY_TO_REPLACEMENT_MODEL,
): string | undefined => {
  if (!savedRaw) {
    return undefined;
  }

  try {
    const saved = JSON.parse(savedRaw);
    if (typeof saved?.llmType !== "string") {
      return undefined;
    }

    const remapped = remapLegacyModelName(
      saved.llmType,
      availableModels,
      replacements,
    );
    if (remapped === saved.llmType) {
      return undefined;
    }

    return JSON.stringify({ ...saved, llmType: remapped });
  } catch {
    return undefined;
  }
};

/**
 * Records that the migration window is closed for the user, so a later
 * deliberate choice of a legacy model is never remapped. Each migration is
 * closed only once one of its replacement models is visible to the user;
 * before that nothing is recorded for it.
 */
export const markModelMigrationsComplete = (
  availableModels: IModelNameHolder[],
  userId?: string,
): void => {
  MODEL_MIGRATIONS.filter((migration) =>
    hasAnyReplacementModel(migration, availableModels),
  ).forEach((migration) => markMigrationComplete(migration, userId));
};

/**
 * One-time localStorage remap of the saved model selection to its replacement
 * (Sonnet 4.5 -> 5.5, Opus 4.5 / 4.6 / 4.7 -> 4.8, Haiku 4.5 -> 5.5). Each migration runs on the
 * first call where one of its replacement models is visible to the user and
 * sets its marker on that call whether or not anything was remapped, so any
 * later explicit pick (including a legacy model) stays as chosen. While none
 * of its replacements is visible a migration touches nothing and sets no marker.
 */
export const applySavedLlmSettingsMigration = (
  availableModels: IModelNameHolder[],
  userId?: string,
): ISavedLlmSettingsMigrationResult => {
  const savedRaw = readStorageItem(STORAGE_KEY_USER_LLM_SETTINGS);
  const pendingMigrations = MODEL_MIGRATIONS.filter((migration) =>
    isMigrationPending(migration, availableModels, userId),
  );
  if (pendingMigrations.length === 0) {
    return { savedRaw, hasRemapped: false };
  }

  pendingMigrations.forEach((migration) =>
    markMigrationComplete(migration, userId),
  );

  const migratedRaw = migrateSavedLlmSettings(
    savedRaw,
    availableModels,
    mergeReplacements(pendingMigrations),
  );
  if (!migratedRaw) {
    return { savedRaw, hasRemapped: false };
  }

  writeStorageItem(STORAGE_KEY_USER_LLM_SETTINGS, migratedRaw);
  return {
    savedRaw: migratedRaw,
    hasRemapped: true,
    previousLlmType: JSON.parse(savedRaw as string).llmType,
    migratedLlmType: JSON.parse(migratedRaw).llmType,
  };
};
